// ============================================================
// GUIDE DU VOYAGEUR — Contenu structuré
// ------------------------------------------------------------
// Chaque rubrique est décrite comme une suite de "blocs" typés
// que <GuideChapter/> sait rendre. C'est la seule source de
// vérité pour le contenu du guide — modifiable ici, sans code.
//
// Types de blocs :
//   - hero-text    : grand display centré (adresse mise en valeur)
//   - hero-facts   : deux faits importants côte à côte (avec icône)
//   - photo-caption: photo + caption (ex: boîte à clés)
//   - callout      : encart mis en avant (icône + libellé + valeur)
//   - note         : paragraphe secondaire discret
//   - list         : liste d'éléments (bullet points élégants)
//   - kv           : liste clé/valeur (2 colonnes)
//   - steps        : liste numérotée (procédure)
//   - warning      : encart d'attention (rubis)
//   - link         : bouton lien externe
//   - tel/mail/insta: liens cliquables tap-to-call / mailto / instagram
// ============================================================
import { MEDIA } from "@/content/media";

export const GUIDE_CONTENT = {
  // ============================================================
  // ADRESSE — hero photo de la porte d'entrée, adresse mise en gros,
  // puis Google Maps, puis parking (regroupé sous "Adresse & parking").
  // ============================================================
  adresse: {
    title: "L'adresse",
    eyebrow: "Adresse & parking",
    hero: MEDIA.refuge.porteArrivee,
    blocks: [
      {
        type: "hero-text",
        eyebrow: "Curieux Refuge",
        value: "21 Rue des Pêches Cavales\nCarrosse — Saint-Gilles-les-Bains\nÎle de la Réunion",
      },
      {
        type: "link",
        label: "Ouvrir dans Google Maps",
        href: "https://www.google.com/maps/search/?api=1&query=21+Rue+des+P%C3%AAches+Cavales+Saint-Gilles-les-Bains",
      },
      {
        type: "callout",
        icon: "Car",
        label: "Parking",
        value:
          "Une place devant la maison est généralement libre — nous vous la gardons dans la mesure du possible. Cette place étant publique, elle peut parfois être occupée : d'autres places sont disponibles à proximité immédiate.",
      },
    ],
  },

  // ============================================================
  // CODE D'ACCÈS — parcours vertical : arrivée 16h autonome ·
  // code envoyé autour de 14h · boîte à clés (photo).
  // ============================================================
  "code-acces": {
    title: "Le code d'accès",
    eyebrow: "Entrer en autonomie",
    blocks: [
      {
        type: "hero-facts",
        facts: [
          { icon: "UserCheck", label: "Arrivée autonome", value: "16 h" },
          { icon: "Clock",     label: "Code envoyé",      value: "Autour de 14 h" },
        ],
      },
      {
        type: "callout",
        icon: "KeyRound",
        label: "Code personnel",
        value:
          "Votre code personnel vous sera envoyé autour de **14 h** le jour de votre arrivée.",
      },
      {
        type: "photo-caption",
        src: MEDIA.refuge.boiteACles,
        alt: "Boîte à clés du Curieux Refuge",
        caption: "Boîte à clés",
        subCaption: "À droite de la porte d'entrée",
      },
    ],
  },

  // ============================================================
  // TRANSPORT — lien vers site officiel Car Jaune (Réunion).
  // ============================================================
  transport: {
    title: "Transport",
    eyebrow: "Se déplacer sur l'île",
    intro:
      "Le refuge est à proximité immédiate d'arrêts de bus qui desservent Saint-Gilles et tout l'ouest de l'île.",
    blocks: [
      {
        type: "list",
        heading: "Arrêts proches du Curieux Refuge",
        items: [
          "École Carrosse",
          "Saint-Gilles Centre",
          "Roches Noires",
        ],
      },
      {
        type: "kv",
        heading: "Option 1 — Application M-Ticket / Car Jaune",
        pairs: [
          ["Paiement", "Directement sur téléphone"],
          ["Validation", "En montant dans le bus"],
        ],
      },
      {
        type: "kv",
        heading: "Option 2 — Ticket à bord",
        pairs: [
          ["Achat", "Directement dans le bus"],
          ["Astuce", "Prévoir de la monnaie"],
        ],
      },
      {
        type: "link",
        label: "Site officiel Car Jaune Réunion",
        href: "https://www.carjaune.re/",
      },
    ],
  },

  // ============================================================
  // WIFI
  // ============================================================
  wifi: {
    title: "Wifi",
    eyebrow: "Rester connecté",
    intro: "Le réseau couvre l'ensemble du refuge, jardin compris.",
    blocks: [
      {
        type: "callout",
        icon: "Wifi",
        label: "Nom du réseau",
        value: "curieuxrefuge",
        mono: true,
      },
      {
        type: "callout",
        icon: "KeyRound",
        label: "Mot de passe",
        value: "CurieuxMotdepasse",
        mono: true,
      },
    ],
  },

  // ============================================================
  // ÉQUIPEMENTS — à revoir ultérieurement (contenu conservé tel quel).
  // ============================================================
  equipements: {
    title: "Équipements",
    eyebrow: "Tout est à portée de main",
    hero: MEDIA.refuge.chambre,
    intro:
      "Le refuge est pensé pour deux à trois voyageurs. Voici ce que vous trouverez sur place.",
    blocks: [
      {
        type: "list",
        heading: "La chambre",
        items: [
          "Lit double 140 × 190 cm",
          "Linge de lit percale de coton",
          "Oreillers moelleux + mémoire de forme",
          "Couette",
          "Climatisation",
        ],
      },
      {
        type: "list",
        heading: "La salle de douche",
        items: [
          "Douche à l'italienne",
          "Serviettes en coton",
          "Gel douche",
          "Sèche-cheveux",
          "Fer à repasser",
        ],
      },
      {
        type: "list",
        heading: "Extérieur & confort",
        items: [
          "Bassin minéral / mini-piscine",
          "Hamac",
          "Rétroprojecteur",
        ],
      },
      {
        type: "section-title",
        title: "Buanderie",
        id: "buanderie",
      },
      {
        type: "callout",
        icon: "shirt",
        label: "Sur demande",
        value: "Accès à la buanderie du refuge : lave-linge et étendoir. L'accès n'est pas ouvert par défaut — merci de nous prévenir pour que nous vous y donnions accès.",
      },
      {
        type: "whatsapp-cta",
        label: "Demander l'accès sur WhatsApp",
        message: "Bonjour, je souhaite avoir accès à la buanderie du refuge (lave-linge + étendoir). Merci !",
        sms: true,
        smsLabel: "Par SMS",
      },
      {
        type: "list",
        heading: "Pour un 3ᵉ voyageur",
        items: ["Lit d'appoint", "Lit parapluie sur demande"],
      },
    ],
  },

  // ============================================================
  // CUISINE — à revoir ultérieurement (contenu conservé tel quel).
  // ============================================================
  cuisine: {
    title: "La cuisine",
    eyebrow: "Cuisiner comme chez soi",
    hero: MEDIA.refuge.cuisine,
    intro:
      "Une cuisine complète pour vos petits-déjeuners, apéros et repas improvisés — tout ce qu'il faut est déjà là.",
    blocks: [
      {
        type: "list",
        heading: "Électroménager",
        items: [
          "Micro-ondes (option grill)",
          "Réfrigérateur",
          "Mini-congélateur",
          "Plaque vitrocéramique 2 feux",
          "Cafetière (café moulu — dosette disponible)",
          "Bouilloire",
        ],
      },
      {
        type: "list",
        heading: "Vaisselle & ustensiles",
        items: [
          "Vaisselle complète pour 3 personnes",
          "Verres à vin, à champagne et à cocktails",
          "Ustensiles de cuisine",
          "Éponge et produits vaisselle fournis",
        ],
      },
      {
        type: "list",
        heading: "Petits plus",
        items: [
          "Sel, poivre, huile d'olive, vinaigre",
          "Café moulu et sucre",
        ],
      },
    ],
  },

  // ============================================================
  // BASSIN — v6 : eyebrow allégé, compare-two cliquable, WhatsApp
  // inline, formulations affinées et highlights sur 48 H / 12 H.
  // ============================================================
  bassin: {
    title: "Le bassin minéral",
    eyebrow: "Écologique & Atypique",
    hero: MEDIA.refuge.bassin,
    intro:
      "Un rituel simple, en plein air — chauffé par le soleil.",
    blocks: [
      // ---- Chiffres clés ----
      {
        type: "stats-row",
        items: [
          { icon: "Sun",     value: "Solaire",  label: "Chauffe-eau" },
          { icon: "Droplets",value: "400 L",    label: "Par jour", subtext: "en fonction de la météo" },
          { icon: "Clock",   value: "35 min",   label: "Remplissage" },
        ],
      },

      // ---- Sélecteur de mode : fonctionnement bassin

      // ================================================================
      // === FONCTIONNEMENT — Sélecteur segmenté eau claire / bain de fleurs
      // ================================================================
      {
        type: "section-title",
        id: "fonctionnement",
        eyebrow: "Fonctionnement",
        title: "Choisir un mode",
      },
      {
        type: "mode-tabs",
        id: "fonctionnement-bassin",
        param: "mode",
        defaultId: "claire",
        tabs: [
          // ============================ Bain à l'eau claire (contenu existant conservé intégralement)
          {
            id: "claire",
            icon: "Droplets",
            label: "Bain eau claire",
            blocks: [
              {
                type: "callout",
                icon: "Droplets",
                label: "À votre arrivée",
                value:
                  "Le bain n'est pas systématiquement rempli — cela permet de préserver la juste température au moment de l'immersion.",
              },
              {
                type: "callout",
                icon: "MessageCircle",
                label: "Souhaitez-vous le trouver prêt ?",
                value:
                  "Nous le préparons avec plaisir. Il sera rempli à moitié afin que vous puissiez ajouter l'eau chaude au moment souhaité.",
                whatsappCta: {
                  label: "Je souhaite le bassin pré-rempli",
                  message:
                    "Bonjour, je souhaiterais que le bassin soit pré-rempli pour notre arrivée au Curieux Refuge, merci !",
                },
              },
              {
                type: "callout",
                icon: "Recycle",
                label: "Préserver l'eau",
                value:
                  "Il n'est pas nécessaire de vider le bassin après chaque utilisation — cela préserve l'eau et s'inscrit dans une démarche écologique.",
              },
              {
                type: "callout",
                icon: "Sparkles",
                label: "Garder l'eau limpide",
                value:
                  "Une douche avant l'immersion garde l'eau claire. Une épuisette est à votre disposition pour retirer feuilles et pétales.",
              },
              {
                type: "callout",
                icon: "Timer",
                label: "Durée d'utilisation",
                value:
                  "Le bain se conserve jusqu'à **48 heures**. Vous pouvez en vider une partie afin d'ajouter de l'eau chaude pour une nouvelle immersion.",
              },
              {
                type: "video-coming",
                label: "Vidéo de remplissage",
              },
              {
                type: "callout",
                icon: "Clock",
                label: "Séjour prolongé",
                value:
                  "Au-delà de **48 heures**, videz entièrement le bain, puis remplissez-le à nouveau en suivant la vidéo.",
              },
              {
                type: "section-title",
                eyebrow: "Départ",
                title: "Avant votre départ",
              },
              {
                type: "callout",
                icon: "Recycle",
                label: "Aucune action requise",
                value:
                  "Il n'est pas nécessaire de vider le bassin avant votre départ. L'eau est filtrée puis réutilisée pour l'arrosage du jardin, dans une démarche respectueuse de l'environnement.",
              },
              {
                type: "warning",
                heading: "Bon à savoir",
                items: [
                  "Éviter de vider entièrement le bain d'eau claire s'il a moins de 48 heures",
                  "Vous pouvez toujours en vider une partie et ajouter de l'eau chaude pour une nouvelle immersion",
                  "Une douche avant l'immersion garde l'eau limpide",
                  "Ne pas ajouter de savon",
                  "Ne pas ajouter d'huile",
                  "Ne pas ajouter de produits moussants",
                ],
              },
              {
                type: "note",
                text:
                  "Au Curieux Refuge, chaque bassin est préparé avec soin afin d'offrir un moment de détente tout en respectant les ressources naturelles.",
              },
            ],
          },

          // ============================ Bain de fleurs — nouveau contenu détaillé
          {
            id: "fleurs",
            icon: "Flower2",
            label: "🌸 Bain de fleurs",
            blocks: [
              {
                type: "callout",
                icon: "Flower2",
                label: "Une expérience éphémère",
                value:
                  "Le bain de fleurs se savoure comme un moment à part.\nParsemé de pétales, il s'agit d'une expérience éphémère qui ne se conserve pas au-delà de **12 heures**.",
              },
              {
                type: "steps",
                heading: "Retrouver l'eau claire",
                intro:
                  "Pour retrouver un bain à l'eau claire, suivez ces étapes simples :",
                items: [
                  "Videz le bain de fleurs en retirant les pierres et le **bouchon gris**.",
                  "Gardez le **bouchon beige** en place et appuyez dessus afin de filtrer l'eau pendant la vidange.",
                  "À l'aide de la **raclette**, rassemblez les fleurs restées dans le bassin.",
                  "Déposez les fleurs dans la **poubelle marron** dédiée au compostage.",
                  "Une fois le bassin vidé, il pourra être rempli à nouveau avec de l'eau claire.",
                ],
              },
              {
                type: "precautions",
                eyebrow: "🌿 Quelques précautions",
                title: "Avant de profiter du bain",
                intro:
                  "Le bain floral est préparé avec des fleurs fraîches soigneusement sélectionnées et adaptées à cette expérience.",
                avoid: [
                  "d'allergie connue aux fleurs, plantes ou parfums naturels",
                  "de peau très sensible, irritée ou présentant une lésion",
                  "de problème dermatologique en cours",
                ],
                tips: [
                  "une durée de bain de 30 à 45 minutes",
                  "un rinçage à l'eau claire après utilisation",
                ],
                footer:
                  "En cas de grossesse, d'allaitement ou de doute médical particulier, il est préférable de demander conseil à un professionnel de santé avant utilisation.",
              },
              {
                type: "callout",
                icon: "Recycle",
                label: "Le jour de votre départ",
                value:
                  "Si vous ne prévoyez pas de nouveau bain, laissez-nous faire — nous nous occupons du nettoyage du bain fleuri.",
              },
              {
                type: "link",
                label: "🌸 Tout savoir sur le bain de fleurs",
                href: "/experiences",
              },
            ],
          },
        ],
      },

      // ================================================================
      // === AVANT VOTRE DÉPART ===
      // ================================================================
      // ================================================================
      // === Note globale visible sur les deux modes (facultatif)
      // ================================================================
    ],
  },

  // ============================================================
  // ÉCLAIRAGE & SON — refonte visuelle : chaque équipement pairé
  // avec son interrupteur ou sa télécommande (miniatures numérotées).
  // ============================================================
  eclairage: {
    title: "Éclairage & son",
    eyebrow: "Lumière, musique, cinéma",
    intro: "Repérez chaque interrupteur en un coup d'œil.",
    blocks: [
      // Lampadaire de la cuisine
      {
        type: "visual-pair",
        eyebrow: "Lampadaire de la cuisine",
        photos: [
          MEDIA.refuge.lampadaireCuisine,
          MEDIA.refuge.interrupteurLampadaire,
        ],
      },

      // Guirlande lumineuse (ex-Terrasse)
      {
        type: "visual-pair",
        eyebrow: "Guirlande lumineuse",
        photos: [
          MEDIA.refuge.guirlandeLumineuse,
          MEDIA.refuge.telecommandeGuirlande,
        ],
        note: "Télécommande à côté du micro-ondes.",
      },

      // Chambre — luminaire + interrupteur (photos réelles fournies)
      {
        type: "visual-pair",
        eyebrow: "Chambre",
        photos: [
          MEDIA.refuge.luminaireChambre,
          MEDIA.refuge.interrupteurChambre,
        ],
        note: "Interrupteur près de la porte de la salle de douche.",
      },

      // Musique — télécommande Yamaha (photo unique centrée)
      {
        type: "visual-pair",
        eyebrow: "Musique",
        photos: [MEDIA.refuge.telecommandeMusique],
        note: "Télécommande Yamaha à disposition · enceinte Bluetooth dans le salon, appairage direct depuis votre téléphone.",
      },

      // Rétroprojecteur — photo produit + photo de la niche avec les 3 télécommandes
      {
        type: "visual-pair",
        eyebrow: "Rétroprojecteur",
        photos: [
          MEDIA.refuge.retroprojecteur,
          MEDIA.refuge.retroClimBougie,
        ],
        note: "Télécommande à disposition. Connexion Bluetooth et accès à vos plateformes de streaming favorites.\nSur la même étagère : télécommande Clim et télécommande pour éteindre les bougies.",
      },
    ],
  },

  // ============================================================
  // CLIMATISATION
  // ============================================================
  climatisation: {
    title: "Climatisation",
    eyebrow: "Trouver la juste fraîcheur",
    intro:
      "Le lieu reste naturellement agréable, mais la climatisation est là pour plus de confort selon vos envies.",
    blocks: [
      {
        type: "callout",
        icon: "Wind",
        label: "Télécommande",
        value: "Sur la table de chevet.",
      },
      {
        type: "list",
        heading: "Bons gestes",
        items: [
          "Fermer portes et fenêtres lorsque la climatisation fonctionne.",
          "Éteindre la climatisation en quittant le refuge.",
        ],
      },
    ],
  },

  // ============================================================
  // RÈGLES
  // ============================================================
  regles: {
    title: "Règles de la maison",
    eyebrow: "Vivre ensemble, respecter le lieu",
    intro:
      "Quatre règles simples pour que chacun profite pleinement du refuge.",
    blocks: [
      {
        type: "kv",
        heading: "Voisinage",
        pairs: [
          [
            "01",
            "Profitez de la musique et des bons moments — tant que tout reste calme et respectueux.",
          ],
        ],
      },
      {
        type: "kv",
        heading: "Attention au feu",
        pairs: [
          [
            "02",
            "L'intérieur est non-fumeur. Vous pouvez fumer à l'extérieur en veillant à préserver les lieux. Les bougies avec flamme sont interdites.",
          ],
        ],
      },
      {
        type: "kv",
        heading: "Invités",
        pairs: [
          [
            "03",
            "Vous pouvez recevoir des visiteurs, sans souci. Le mobilier est conçu pour 2 à 3 personnes selon votre réservation.",
          ],
        ],
      },
      {
        type: "kv",
        heading: "Ménage & vaisselle",
        pairs: [
          [
            "04",
            "La vaisselle doit être laissée propre dans l'égouttoir — nous nous chargeons de la ranger dans les placards.",
          ],
        ],
      },
    ],
  },

  // ============================================================
  // URGENCES
  // ============================================================
  urgences: {
    title: "En cas d'urgence",
    eyebrow: "Numéros et gestes clés",
    intro:
      "Nous espérons que vous n'en aurez pas besoin — mais toutes les informations utiles sont ici.",
    blocks: [
      {
        type: "callout",
        icon: "Flame",
        label: "Extincteur",
        value: "Sous l'évier de la cuisine.",
      },
      {
        type: "kv",
        heading: "Numéros d'urgence",
        pairs: [
          ["SAMU", "15"],
          ["Police", "17"],
          ["Pompiers", "18"],
        ],
      },
      {
        type: "list",
        heading: "Santé — à proximité",
        items: [
          "Hôpital CHOR Saint-Paul",
          "Pharmacie de Roquefeuil",
          "Pharmacie de l'Océan",
          "Pharmacie de Saint-Gilles",
          "SOS Médecin Saint-Paul (médecin de garde)",
        ],
      },
    ],
  },

  // ============================================================
  // CONTACT — numéros cliquables (tap-to-call, tap-to-message)
  // ============================================================
  contact: {
    title: "Contact & réseaux",
    eyebrow: "Écrire à Eva & Gabrielle",
    intro:
      "Nous restons joignables tout au long de votre séjour, avec plaisir.",
    blocks: [
      {
        type: "tel",
        label: "Appeler Eva & Gabrielle",
        value: "+262 692 79 31 55",
        href: "tel:+262692793155",
        secondary: {
          label: "WhatsApp",
          value: "Nous écrire",
          href: "https://wa.me/262692793155",
        },
      },
      {
        type: "mail",
        label: "Email",
        value: "curieuxrefuge.carosse@gmail.com",
        href: "mailto:curieuxrefuge.carosse@gmail.com",
      },
      {
        type: "insta",
        label: "Instagram",
        value: "@curieuxrefugesaintgilles",
        href: "https://instagram.com/curieuxrefugesaintgilles",
      },
    ],
  },

  // ============================================================
  // DÉPART
  // ============================================================
  depart: {
    title: "Le départ",
    eyebrow: "Refermer la porte du refuge",
    hero: MEDIA.refuge.jardinEntree,
    intro:
      "Un départ simple et sans stress — nous nous chargeons du reste.",
    blocks: [
      {
        type: "callout",
        icon: "Clock",
        label: "Heure de départ",
        value: "12 h",
      },
      {
        type: "list",
        heading: "Ce qui est déjà pris en charge",
        items: [
          "Vous pouvez laisser le lit tel quel — nous nous occupons du linge.",
          "Déposez simplement votre vaisselle propre sur l'égouttoir, nous la rangerons.",
        ],
      },
      {
        type: "steps",
        heading: "Avant de partir",
        items: [
          "Vérifiez que tout est fermé et éteint.",
          "Remettez les clés dans la boîte à clés.",
          "Brouillez le code de la boîte.",
          "Envoyez-nous un petit message pour nous prévenir.",
        ],
      },
    ],
  },
};

export default GUIDE_CONTENT;
